/**
 * Every read and write the application performs against storage.
 *
 * `db.ts` owns the schema; this file owns the questions. Nothing outside these two files
 * should import `idb` or know a store name — that way a schema change has exactly one
 * blast radius, and the sketchbook UI can be written against a stable vocabulary
 * (`listSketches`, `getSketch`, `updateSketch`) that will outlive the storage layer.
 *
 * Everything here resolves rather than rejects when storage is unavailable: in private
 * browsing the driver is in-memory, writes succeed for the life of the tab, and
 * `storageStatus().durable` is false. Callers do not branch on that; the UI shows a chip.
 */

import type { ModelChoice, PreferenceHints, SessionNode, SessionState } from '@hc/shared';
import { MODEL_CHOICES, createEmptyPreferenceHints } from '@hc/shared';
import {
  COLLECT_RECOGNITIONS_KEY,
  CORRECTION_LOG_CAP,
  HOST_SETTINGS_KEY,
  VERDICT_LOG_CAP,
  getDb,
  PREFERENCES_KEY,
  PREFERENCE_TALLIES_KEY,
  verdictOf,
  type CorrectionLogEntry,
  type FavouriteTransition,
  type PreferenceTallies,
  type RecognitionVerdict,
  type SessionSnapshotRecord,
  type SketchExplanation,
  type SketchRecord,
} from './db.js';

/** `crypto.randomUUID` where it exists (browsers, Node 22), with a plain fallback. */
export function newId(): string {
  const cryptoRef = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof cryptoRef?.randomUUID === 'function') return cryptoRef.randomUUID();
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// Session snapshots — refresh recovery
// ---------------------------------------------------------------------------

/**
 * Write the autosave for a session. Called from the debounced persistence effect, never
 * from a component.
 */
export async function saveSessionSnapshot(
  state: SessionState,
  savedAt = Date.now(),
): Promise<void> {
  const db = await getDb();
  await db.put<SessionSnapshotRecord>('sessionSnapshot', {
    sessionId: state.sessionId,
    savedAt,
    state,
  });
}

export async function loadSessionSnapshot(
  sessionId: string,
): Promise<SessionSnapshotRecord | null> {
  const db = await getDb();
  return (await db.get<SessionSnapshotRecord>('sessionSnapshot', sessionId)) ?? null;
}

/** The snapshot to offer on a cold start: the most recently saved one. */
export async function latestSessionSnapshot(): Promise<SessionSnapshotRecord | null> {
  const db = await getDb();
  const rows = await db.getAllFromIndex<SessionSnapshotRecord>(
    'sessionSnapshot',
    'by-savedAt',
    true,
  );
  return rows[0] ?? null;
}

/** Newest first. */
export async function listSessionSnapshots(): Promise<SessionSnapshotRecord[]> {
  const db = await getDb();
  return db.getAllFromIndex<SessionSnapshotRecord>('sessionSnapshot', 'by-savedAt', true);
}

export async function deleteSessionSnapshot(sessionId: string): Promise<void> {
  const db = await getDb();
  await db.delete('sessionSnapshot', sessionId);
}

/**
 * Snapshots are cheap but not free, and only the current session is ever restored.
 * Keeping a handful lets "resume yesterday's session" work without unbounded growth.
 */
export const SESSION_SNAPSHOT_CAP = 10;

export async function pruneSessionSnapshots(cap = SESSION_SNAPSHOT_CAP): Promise<number> {
  const db = await getDb();
  const rows = await db.getAllFromIndex<SessionSnapshotRecord>(
    'sessionSnapshot',
    'by-savedAt',
    true,
  );
  const doomed = rows.slice(cap);
  if (doomed.length === 0) return 0;
  await db.deleteMany(
    'sessionSnapshot',
    doomed.map((row) => row.sessionId),
  );
  return doomed.length;
}

// ---------------------------------------------------------------------------
// Sketches — the sketchbook
// ---------------------------------------------------------------------------

/** What a listing needs. Deliberately without `nodes` or `audio`, which are heavy. */
export interface SketchSummary {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  intention?: string;
  tags: string[];
  nodeCount: number;
  favouriteCount: number;
  hasAudio: boolean;
  /** The first few chord symbols, for a one-line preview in the list. */
  preview: string[];
}

export interface ListSketchesOptions {
  /** Default `'newest'`. */
  sort?: 'newest' | 'oldest' | 'title';
  tag?: string;
  /** Case-insensitive substring over title, notes, intention and chord symbols. */
  search?: string;
  limit?: number;
  offset?: number;
}

export const SKETCH_PREVIEW_LENGTH = 6;

function toSummary(sketch: SketchRecord): SketchSummary {
  const summary: SketchSummary = {
    id: sketch.id,
    title: sketch.title,
    createdAt: sketch.createdAt,
    updatedAt: sketch.updatedAt,
    tags: [...sketch.tags],
    nodeCount: sketch.nodes.length,
    favouriteCount: sketch.favouriteTransitions.length,
    hasAudio: sketch.audio !== undefined,
    preview: sketch.nodes.slice(0, SKETCH_PREVIEW_LENGTH).map((node) => node.chord.symbol),
  };
  if (sketch.intention !== undefined) summary.intention = sketch.intention;
  return summary;
}

function matchesSearch(sketch: SketchRecord, needle: string): boolean {
  const haystack = [
    sketch.title,
    sketch.userNotes,
    sketch.intention ?? '',
    sketch.tags.join(' '),
    sketch.nodes.map((node) => node.chord.symbol).join(' '),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle.toLowerCase());
}

export async function listSketches(options: ListSketchesOptions = {}): Promise<SketchSummary[]> {
  const db = await getDb();
  const sort = options.sort ?? 'newest';
  let rows =
    sort === 'title'
      ? await db.getAll<SketchRecord>('sketches')
      : await db.getAllFromIndex<SketchRecord>('sketches', 'by-updatedAt', sort === 'newest');

  if (sort === 'title') {
    rows = rows.sort((a, b) => a.title.localeCompare(b.title));
  }
  if (options.tag) rows = rows.filter((row) => row.tags.includes(options.tag as string));
  if (options.search) rows = rows.filter((row) => matchesSearch(row, options.search as string));

  const offset = options.offset ?? 0;
  const limited =
    options.limit === undefined ? rows.slice(offset) : rows.slice(offset, offset + options.limit);
  return limited.map(toSummary);
}

export async function countSketches(): Promise<number> {
  const db = await getDb();
  return db.count('sketches');
}

export async function getSketch(id: string): Promise<SketchRecord | null> {
  const db = await getDb();
  return (await db.get<SketchRecord>('sketches', id)) ?? null;
}

/** Everything a caller may set when keeping a sketch. `id` and the timestamps are ours. */
export interface NewSketch {
  title: string;
  nodes: SessionNode[];
  sessionId?: string;
  intention?: string;
  userNotes?: string;
  tags?: string[];
  favouriteTransitions?: FavouriteTransition[];
  explanations?: SketchExplanation[];
  audio?: Blob;
  audioMimeType?: string;
  audioDurationMs?: number;
  createdAt?: number;
}

export async function createSketch(input: NewSketch): Promise<SketchRecord> {
  const now = input.createdAt ?? Date.now();
  const sketch: SketchRecord = {
    id: newId(),
    title: input.title,
    createdAt: now,
    updatedAt: now,
    nodes: input.nodes,
    userNotes: input.userNotes ?? '',
    tags: input.tags ?? [],
    favouriteTransitions: input.favouriteTransitions ?? [],
    explanations: input.explanations ?? [],
  };
  if (input.sessionId !== undefined) sketch.sessionId = input.sessionId;
  if (input.intention !== undefined) sketch.intention = input.intention;
  if (input.audio !== undefined) sketch.audio = input.audio;
  if (input.audioMimeType !== undefined) sketch.audioMimeType = input.audioMimeType;
  if (input.audioDurationMs !== undefined) sketch.audioDurationMs = input.audioDurationMs;

  const db = await getDb();
  await db.put('sketches', sketch);
  return sketch;
}

/**
 * Keep the current session. The title defaults to the intention, then to the first few
 * chords — a sketchbook full of "Untitled 4" is a sketchbook nobody opens.
 */
export async function createSketchFromSession(
  state: SessionState,
  extras: Partial<NewSketch> = {},
): Promise<SketchRecord> {
  const preview = state.path
    .slice(0, 4)
    .map((node) => node.chord.symbol)
    .join(' – ');
  const input: NewSketch = {
    title: extras.title ?? state.intention ?? (preview || 'Untitled sketch'),
    nodes: extras.nodes ?? state.path,
    sessionId: state.sessionId,
    ...extras,
  };
  if (state.intention !== undefined && input.intention === undefined)
    input.intention = state.intention;
  return createSketch(input);
}

/** Fields a caller may change after the fact. */
export type SketchPatch = Partial<
  Pick<
    SketchRecord,
    | 'title'
    | 'userNotes'
    | 'tags'
    | 'intention'
    | 'favouriteTransitions'
    | 'explanations'
    | 'nodes'
    | 'audio'
    | 'audioMimeType'
    | 'audioDurationMs'
  >
>;

export async function updateSketch(id: string, patch: SketchPatch): Promise<SketchRecord | null> {
  const db = await getDb();
  const existing = await db.get<SketchRecord>('sketches', id);
  if (!existing) return null;
  const updated: SketchRecord = { ...existing, ...patch, updatedAt: Date.now() };
  await db.put('sketches', updated);
  return updated;
}

export async function deleteSketch(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('sketches', id);
}

export async function addFavouriteTransition(
  id: string,
  transition: FavouriteTransition,
): Promise<SketchRecord | null> {
  const existing = await getSketch(id);
  if (!existing) return null;
  return updateSketch(id, {
    favouriteTransitions: [...existing.favouriteTransitions, transition],
  });
}

export async function removeFavouriteTransition(
  id: string,
  predicate: (transition: FavouriteTransition) => boolean,
): Promise<SketchRecord | null> {
  const existing = await getSketch(id);
  if (!existing) return null;
  return updateSketch(id, {
    favouriteTransitions: existing.favouriteTransitions.filter((t) => !predicate(t)),
  });
}

export async function addSketchExplanation(
  id: string,
  explanation: SketchExplanation,
): Promise<SketchRecord | null> {
  const existing = await getSketch(id);
  if (!existing) return null;
  return updateSketch(id, { explanations: [...existing.explanations, explanation] });
}

/** Every tag in use, with counts, most used first — for the filter row. */
export async function listTags(): Promise<Array<{ tag: string; count: number }>> {
  const db = await getDb();
  const rows = await db.getAll<SketchRecord>('sketches');
  const counts = new Map<string, number>();
  for (const row of rows) {
    for (const tag of row.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}

export async function attachSketchAudio(
  id: string,
  audio: Blob,
  meta: { mimeType?: string; durationMs?: number } = {},
): Promise<SketchRecord | null> {
  const patch: SketchPatch = { audio };
  if (meta.mimeType !== undefined) patch.audioMimeType = meta.mimeType;
  if (meta.durationMs !== undefined) patch.audioDurationMs = meta.durationMs;
  return updateSketch(id, patch);
}

export async function getSketchAudio(id: string): Promise<Blob | null> {
  const sketch = await getSketch(id);
  return sketch?.audio ?? null;
}

/** JSON export. The audio Blob is deliberately not included — it is not JSON. */
export async function exportSketch(id: string): Promise<string | null> {
  const sketch = await getSketch(id);
  if (!sketch) return null;
  const { audio: _audio, ...portable } = sketch;
  return JSON.stringify(
    { kind: 'harmony-companion/sketch', version: 1, sketch: portable },
    null,
    2,
  );
}

export async function importSketch(json: string): Promise<SketchRecord> {
  const parsed = JSON.parse(json) as { sketch?: Partial<SketchRecord> };
  const sketch = parsed.sketch ?? (parsed as unknown as Partial<SketchRecord>);
  if (!Array.isArray(sketch.nodes)) throw new Error('not a sketch: no nodes');
  const input: NewSketch = {
    title: sketch.title ?? 'Imported sketch',
    nodes: sketch.nodes,
    userNotes: sketch.userNotes ?? '',
    tags: sketch.tags ?? [],
    favouriteTransitions: sketch.favouriteTransitions ?? [],
    explanations: sketch.explanations ?? [],
  };
  if (sketch.intention !== undefined) input.intention = sketch.intention;
  return createSketch(input);
}

// ---------------------------------------------------------------------------
// Preferences
// ---------------------------------------------------------------------------

export async function loadPreferenceHints(): Promise<PreferenceHints> {
  const db = await getDb();
  const stored = await db.get<PreferenceHints>('preferences', PREFERENCES_KEY);
  // Merge onto a fresh set so a movement kind added since the last release is present
  // with a zero count rather than `undefined`.
  const empty = createEmptyPreferenceHints();
  if (!stored) return empty;
  return {
    ...empty,
    ...stored,
    takenKindCounts: { ...empty.takenKindCounts, ...stored.takenKindCounts },
  };
}

export async function savePreferenceHints(hints: PreferenceHints): Promise<void> {
  const db = await getDb();
  await db.put('preferences', hints, PREFERENCES_KEY);
}

export function createEmptyPreferenceTallies(): PreferenceTallies {
  return { keyCounts: {}, explanationsOffered: 0, explanationsOpened: 0, sessionCount: 0 };
}

export async function loadPreferenceTallies(): Promise<PreferenceTallies> {
  const db = await getDb();
  const stored = await db.get<PreferenceTallies>('preferences', PREFERENCE_TALLIES_KEY);
  return { ...createEmptyPreferenceTallies(), ...stored };
}

export async function savePreferenceTallies(tallies: PreferenceTallies): Promise<void> {
  const db = await getDb();
  await db.put('preferences', tallies, PREFERENCE_TALLIES_KEY);
}

/**
 * Forget everything learned about this player. Both records go in one transaction so a
 * reset can never half-happen and leave the hints disagreeing with their own tallies.
 */
export async function resetPreferences(): Promise<PreferenceHints> {
  const db = await getDb();
  const hints = createEmptyPreferenceHints();
  await db.putMany([
    { store: 'preferences', value: hints, key: PREFERENCES_KEY },
    { store: 'preferences', value: createEmptyPreferenceTallies(), key: PREFERENCE_TALLIES_KEY },
  ]);
  return hints;
}

// ---------------------------------------------------------------------------
// Collection mode
// ---------------------------------------------------------------------------

/** Wrapped rather than stored bare, so the record can grow a field without a migration. */
interface CollectRecognitionsRecord {
  enabled: boolean;
}

/**
 * The stored choice, or `null` when nobody has made one — which is not the same as
 * `false`. Off is the default, and the default experience is today's screen.
 */
export async function loadCollectRecognitions(): Promise<boolean | null> {
  const db = await getDb();
  const stored = await db.get<CollectRecognitionsRecord>('preferences', COLLECT_RECOGNITIONS_KEY);
  return typeof stored?.enabled === 'boolean' ? stored.enabled : null;
}

export async function saveCollectRecognitions(enabled: boolean): Promise<void> {
  const db = await getDb();
  await db.put<CollectRecognitionsRecord>('preferences', { enabled }, COLLECT_RECOGNITIONS_KEY);
}

// ---------------------------------------------------------------------------
// The host's own settings
// ---------------------------------------------------------------------------

/**
 * The settings `state/sessionHost.tsx` holds that should survive a reload.
 *
 * Every field is optional and a missing one means "never chosen", which is not the same as
 * its default — the host applies its defaults and only overrides them with what was
 * actually stored. `prefetch` is deliberately absent: it is a token-saving choice made for
 * one sitting, and `speakReplies` belongs to `commands/voice.ts`, which keeps it in
 * localStorage so it can be read before any database opens.
 */
export interface HostSettingsRecord {
  soundOnTheGo?: boolean;
  /** Model-generated suggestions instead of the built-in engine. Absent means off. */
  aiCompanion?: boolean;
  modelChoice?: ModelChoice;
  showPrompts?: boolean;
  /**
   * The last adventurousness the player set, from the home screen's slider or the drawer's.
   * A standing preference rather than part of a progression: the home screen offers it as
   * the kind of session to have, and a reload that put it back at "balanced" would make it
   * look forgotten exactly where it is offered.
   */
  adventurousness?: number;
}

function isModelChoice(value: unknown): value is ModelChoice {
  return MODEL_CHOICES.includes(value as ModelChoice);
}

export async function loadHostSettings(): Promise<HostSettingsRecord> {
  const db = await getDb();
  const stored = await db.get<HostSettingsRecord>('preferences', HOST_SETTINGS_KEY);
  if (!stored) return {};
  // Read field by field rather than trusted wholesale: this record is written by a build
  // that may be older than this one, and a `modelChoice` naming a tier that no longer
  // exists would be sent to the server on every request until somebody opened settings.
  const settings: HostSettingsRecord = {};
  if (typeof stored.soundOnTheGo === 'boolean') settings.soundOnTheGo = stored.soundOnTheGo;
  if (typeof stored.aiCompanion === 'boolean') settings.aiCompanion = stored.aiCompanion;
  if (typeof stored.showPrompts === 'boolean') settings.showPrompts = stored.showPrompts;
  if (isModelChoice(stored.modelChoice)) settings.modelChoice = stored.modelChoice;
  if (
    typeof stored.adventurousness === 'number' &&
    Number.isFinite(stored.adventurousness) &&
    stored.adventurousness >= 0 &&
    stored.adventurousness <= 1
  ) {
    settings.adventurousness = stored.adventurousness;
  }
  return settings;
}

/** Merged with what is already stored, so one changed toggle does not forget the others. */
export async function saveHostSettings(patch: HostSettingsRecord): Promise<void> {
  const db = await getDb();
  const current = await loadHostSettings();
  await db.put<HostSettingsRecord>('preferences', { ...current, ...patch }, HOST_SETTINGS_KEY);
}

// ---------------------------------------------------------------------------
// The verdict log
// ---------------------------------------------------------------------------

/**
 * How many records to keep, given what the device is being used for right now.
 *
 * Read from storage on every write rather than plumbed down from the session, so that
 * every writer — the correction sheet, the ✓/✗ chrome, anything later — agrees about the
 * cap without a second copy of the setting to keep in step. A single `get` against an
 * in-process database, a handful of times a minute, is not worth the coupling it saves.
 */
export async function verdictLogCap(): Promise<number> {
  return (await loadCollectRecognitions()) ? VERDICT_LOG_CAP : CORRECTION_LOG_CAP;
}

export interface AppendVerdictOptions {
  /** Overrides the cap. Tests and one-off imports; the app lets `verdictLogCap` decide. */
  cap?: number;
}

/**
 * Record one verdict on one recognition.
 *
 * Eviction is **oldest first and only oldest**: the log is a chronology, and dropping
 * anything else would silently bias whatever is later trained on it. Keys are
 * auto-incremented, so ascending key order is insertion order.
 */
export async function appendVerdict(
  entry: CorrectionLogEntry,
  options: AppendVerdictOptions = {},
): Promise<void> {
  const cap = Math.max(1, options.cap ?? (await verdictLogCap()));
  const db = await getDb();
  await db.put('correctionLog', entry);
  const keys = await db.keys('correctionLog');
  if (keys.length > cap) {
    await db.deleteMany('correctionLog', keys.slice(0, keys.length - cap));
  }
}

/**
 * Record a misrecognition — the correction sheet's path into the same log.
 *
 * Kept as its own name because "the player told us it was wrong" is the one verdict with
 * a second chord attached, and because it is what the persistence port is called.
 */
export async function appendCorrection(
  entry: CorrectionLogEntry,
  options?: AppendVerdictOptions,
): Promise<void> {
  return appendVerdict({ verdict: 'corrected', ...entry }, options);
}

export interface ListVerdictsOptions {
  limit?: number;
  /** Only this verdict. Legacy records with no field read as `'corrected'`. */
  verdict?: RecognitionVerdict;
}

/** Newest first. */
export async function listVerdicts(
  options: ListVerdictsOptions = {},
): Promise<CorrectionLogEntry[]> {
  const db = await getDb();
  const rows = await db.getAllFromIndex<CorrectionLogEntry>('correctionLog', 'by-ts', true);
  const matching = options.verdict
    ? rows.filter((row) => verdictOf(row) === options.verdict)
    : rows;
  return options.limit === undefined ? matching : matching.slice(0, options.limit);
}

export interface VerdictCounts {
  corrected: number;
  confirmed: number;
  unattended: number;
  total: number;
}

/** What the tuning screen shows, and the honest denominator for any of it. */
export async function countVerdicts(): Promise<VerdictCounts> {
  const db = await getDb();
  const rows = await db.getAll<CorrectionLogEntry>('correctionLog');
  const counts: VerdictCounts = { corrected: 0, confirmed: 0, unattended: 0, total: rows.length };
  for (const row of rows) counts[verdictOf(row)] += 1;
  return counts;
}

/** Everything, including the corrections. Its own affordance, behind its own confirm. */
export async function clearVerdictLog(): Promise<void> {
  const db = await getDb();
  await db.clear('correctionLog');
}

export type {
  CorrectionLogEntry,
  FavouriteTransition,
  PreferenceTallies,
  RecognitionVerdict,
  SessionSnapshotRecord,
  SketchExplanation,
  SketchRecord,
};
